/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useState } from 'react';
import { LinkButton, PortalSpinner, TextColumn, useGlobalLang, useGlobalServices } from '@veeam-vspc/shared/components';
import { EVbFlrRestoreStatus } from '@veeam-vspc/models/web-controllers';
import { HttpMethods } from '@veeam-vspc/shared/core';
import { ColumnTexts } from '@veeam-vspc/shared/enums';

import type {
    GetVbRestoreSessionWorkerLinkParams,
    VbFlrRestoreStatusModel,
    EPublicCloudRestoreType,
} from '@veeam-vspc/models/web-controllers';
import type { RequestSuccessResponse } from '@veeam-vspc/shared/interfaces';
import type { VspcLang } from 'configs/languages';

import { StatusColumn } from 'views/components/columns/StatusColumn';

export interface FlrPortalColumnProps {
    instanceUid: string;
    vbServerId: string;
    restoreType: EPublicCloudRestoreType;
    restoreStatusModel: VbFlrRestoreStatusModel | null;
}

export const FlrPortalColumn: React.FC<FlrPortalColumnProps> = ({ instanceUid, restoreType, restoreStatusModel, vbServerId }) => {
    const lang = useGlobalLang<VspcLang>();
    const { transportService, notificationService } = useGlobalServices();
    const [loading, setLoading] = useState(false);

    if (!restoreStatusModel) {
        return (
            ColumnTexts.DashSymbol
        );
    }

    const { sessionId, status } = restoreStatusModel;

    if (status === EVbFlrRestoreStatus.Pending) {
        return (
            <StatusColumn status={status} />
        );
    }

    return (
        <>
            <TextColumn>
                <LinkButton
                    onClick={() => {
                        setLoading(true);

                        transportService.request<GetVbRestoreSessionWorkerLinkParams, RequestSuccessResponse<string>>(
                            '/VbPlugin/GetFlrSessionWorkerLink',
                            {
                                itemUid: instanceUid,
                                vbServerId,
                                restoreSessionUid: sessionId,
                                restoreType,
                                backUrl: window.location.href,
                            },
                        )
                            .then((resp) => {
                                const linkUrl = (resp as RequestSuccessResponse<string>).data as string;

                                return fetch(linkUrl, { method: HttpMethods.Get })
                                    .then(resp => resp.ok ? linkUrl : Promise.reject('unable'))
                                    .catch(() => Promise.reject('unable'));
                            })
                            .then((linkUrl) => {
                                setLoading(false);
                                window.open(linkUrl, '_blank', 'noopener,noreferrer');
                            })
                            .catch((err) => {
                                setLoading(false);

                                if (err === 'unable') {
                                    notificationService.error(
                                        lang.FILE_LEVEL_RESTORE_PORTAL,
                                        lang.UNABLE_TO_PERFORM_THE_OPERATION_NETWORK_SETTINGS_FOR,
                                    );
                                } else {
                                    err && console.error(err);
                                }
                            });
                    }}
                >
                    {lang.RESTORE_PORTAL}
                </LinkButton>
            </TextColumn>

            {loading && <PortalSpinner />}
        </>
    );
};
